import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import SegmentedTabs from 'components/UI/SegmentedTabs';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import TopicsConfigSection from 'pages/BusinessUnitConfig/components/TopicsConfigSection';
import DemoChatPanel from 'pages/Demochat/DemoChatPanel';
import { useState } from 'react';
import { useGetBusinessUnitQuery, useUpdateBusinessUnitTaggingConfigMutation } from 'services/api/businessUnits.api';
import type { BusinessUnitConversationTaggingConfig } from 'services/types/businessUnit';
import { MOBILE_PAGE_PT, NAVY } from 'styles/tokens';

import PromptBuilder from './components/PromptBuilder';

const PromptSections = () => {
  const { selectedBusinessUnitId: businessUnitId } = useSelectedBusinessUnit();
  const [activeTab, setActiveTab] = useState(0);

  const { data: businessUnit, refetch: refetchBusinessUnit } = useGetBusinessUnitQuery(businessUnitId as number, {
    skip: !businessUnitId,
  });
  const [updateTaggingConfig] = useUpdateBusinessUnitTaggingConfigMutation();

  const handleSaveTopicsConfig = (
    conversationTaggingConfig: BusinessUnitConversationTaggingConfig
  ): Promise<unknown> | undefined => {
    if (!businessUnitId) return Promise.resolve();

    return updateTaggingConfig({ businessUnitId, conversationTaggingConfig })
      .unwrap()
      .then(() => refetchBusinessUnit());
  };

  const companyIdentifier = businessUnit?.pusherIdentification;

  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 3 },
        pt: { xs: `${MOBILE_PAGE_PT}px`, md: 2 },
        pb: { xs: 1.5, md: 3 },
        height: { md: '100vh' },
        minHeight: { xs: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'auto', md: 'hidden' },
      }}
    >
      {businessUnitId ? (
        <>
          {/* Tab bar */}
          <Box sx={{ mb: 1.5 }}>
            <SegmentedTabs tabs={['Prompt', 'Tags', 'Chat']} value={activeTab} onChange={setActiveTab} />
          </Box>

          {/* Tab content */}
          <Fade in={activeTab === 0} mountOnEnter unmountOnExit timeout={300}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <PromptBuilder businessUnitId={businessUnitId} />
            </Box>
          </Fade>
          <Fade in={activeTab === 1} mountOnEnter unmountOnExit timeout={300}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto' }}>
              <TopicsConfigSection
                conversationTaggingConfig={businessUnit?.conversationTaggingConfig}
                onSave={handleSaveTopicsConfig}
              />
            </Box>
          </Fade>
          <Fade in={activeTab === 2} mountOnEnter unmountOnExit timeout={300}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {companyIdentifier ? (
                <DemoChatPanel companyIdentifier={companyIdentifier} embedded />
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body1" sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
                    Esta unidad de negocio no tiene un identificador de chat configurado.
                  </Typography>
                </Box>
              )}
            </Box>
          </Fade>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography variant="h5" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}>
            Selecciona una unidad de negocio para ver la información que Relif tiene disponible
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PromptSections;
